import { inject, Injectable } from '@angular/core';
import gql from 'graphql-tag';
import {
	MaintenanceSelectionTypes,
	DeviationTypeTranslationKeys,
	TimeframeTypeTranslationKeys,
	CostAllocationTypeTranslationKeys,
	RiskLevelTypeTranslationKeys,
	ConsequenceRatingChoicesTypeTranslationKeys,
	MaintenanceSelectionConfiguration,
} from '@app/pages/cmms/configs/cmms-maintenance.enums';
import { Apollo } from 'apollo-angular';
import { map, take } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import {
	loadedNsFieldsKeyValues,
	startLoadNsFieldsKeyValues,
} from '@app/pages/cmms/state/cmms.actions';
import {
	cmmsNsFieldsKeyValues,
	nameNsFieldsDescriptionList,
} from '@app/pages/cmms/state/cmms.selectors';

const EnumValuesFragment = gql`
	fragment EnumValuesFragment on __Type {
		name
		enumValues {
			name
			description
		}
	}
`;

const queryHashMap = {
	DeviationTypeChoices: gql`
		query getEnumValues {
			__type(name: "DeviationTypeChoices") {
				...EnumValuesFragment
			}
		}
		${EnumValuesFragment}
	`,
	TimeframeChoices: gql`
		query getEnumValues {
			__type(name: "TimeframeChoices") {
				...EnumValuesFragment
			}
		}
		${EnumValuesFragment}
	`,
	CostAllocationChoices: gql`
		query getEnumValues {
			__type(name: "CostAllocationChoices") {
				...EnumValuesFragment
			}
		}
		${EnumValuesFragment}
	`,
	RiskLevelChoices: gql`
		query getEnumValues {
			__type(name: "RiskLevelChoices") {
				...EnumValuesFragment
			}
		}
		${EnumValuesFragment}
	`,
	ConsequenceRatingChoices: gql`
		query getEnumValues {
			__type(name: "ConsequenceRatingChoices") {
				...EnumValuesFragment
			}
		}
		${EnumValuesFragment}
	`,
};

@Injectable({
	providedIn: 'root',
})
export class MaintenanceSelectorService {
	private readonly store$ = inject(Store);
	private readonly apollo = inject(Apollo);

	get nameDescriptionList() {
		return this.store$.select(nameNsFieldsDescriptionList);
	}

	getConfiguration(
		type: MaintenanceSelectionTypes
	): Observable<{ label: string; enumValues: { name: string; description: string }[] }> {
		return this.store$.select(cmmsNsFieldsKeyValues).pipe(
			map(config => {
				if (!config) {
					return null;
				}

				return config[type] || null;
			})
		);
	}

	startLoadNsFieldsKeyValues() {
		this.store$.dispatch(startLoadNsFieldsKeyValues());
	}

	/**
	 * Load al NS fields enum values based on the keys in the MaintenanceSelectionConfiguration object
	 */
	loadConfigurations(): void {
		const requests = {};
		const nameDescriptionList = {};

		for (const requestsKey in MaintenanceSelectionConfiguration) {
			requests[requestsKey] = this.loadChoiceList(
				requestsKey as MaintenanceSelectionTypes
			).pipe(
				take(1),
				map((response: any) => {
					return response.data.__type;
				})
			);
		}

		forkJoin(requests)
			.pipe(take(1))
			.subscribe({
				next: (responses: { [key in MaintenanceSelectionTypes]: any }) => {
					for (const key in responses) {
						const enumValues = responses[key].enumValues;
						const mappedEnumValues = enumValues.map(({ name }) => ({
							name,
							description: this.getTranslationKeyForEnum(
								key as MaintenanceSelectionTypes,
								name
							),
						}));

						MaintenanceSelectionConfiguration[key].enumValues = mappedEnumValues;
						mappedEnumValues.forEach(({ name, description }) => {
							nameDescriptionList[name] = description;
						});
					}

					this.store$.dispatch(
						loadedNsFieldsKeyValues({
							nsFieldsKeyValues: structuredClone(MaintenanceSelectionConfiguration),
							nameDescriptionList,
						})
					);
				},
				error: error => {
					console.error(error);
				},
			});
	}

	private loadChoiceList(type: MaintenanceSelectionTypes) {
		return this.apollo.query<{ name: string; description: string }[]>({
			query: queryHashMap[type],
		});
	}

	getTranslationKeyForEnum(type: MaintenanceSelectionTypes, name: string): string {
		if (!name) return '';

		switch (type) {
			case 'DeviationTypeChoices':
				return DeviationTypeTranslationKeys[name];
			case 'TimeframeChoices':
				return TimeframeTypeTranslationKeys[name];
			case 'CostAllocationChoices':
				return CostAllocationTypeTranslationKeys[name];
			case 'RiskLevelChoices':
				return RiskLevelTypeTranslationKeys[name];
			case 'ConsequenceRatingChoices':
				return ConsequenceRatingChoicesTypeTranslationKeys[name];
			default:
				return name.toLowerCase();
		}
	}
}
